import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Button } from "@/components/ui/button";
import { services, stats } from "@/data/content";
import { HeroVisual } from "@/components/HeroVisual";
import { SEO } from "@/components/SEO";

export default function Home() {
  return (
    <div data-testid="home-page">
      <SEO path="/" />
      <section className="relative overflow-hidden px-5 pb-20 pt-36 sm:px-8 lg:pt-44">
        <div className="mx-auto grid max-w-7xl items-center gap-14 lg:grid-cols-[1.05fr_0.95fr]">
          <Reveal>
            <p className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-4 py-2 text-xs font-semibold uppercase tracking-[0.24em] text-primary" data-testid="home-hero-eyebrow">
              <Sparkles className="h-4 w-4" aria-hidden="true" /> Transformation numérique sécurisée
            </p>
            <h1 className="text-balance mt-7 max-w-4xl text-4xl font-semibold tracking-tighter sm:text-5xl lg:text-6xl" data-testid="home-hero-title">
              Le partenaire numérique des PME ambitieuses.
            </h1>
            <p className="mt-7 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg" data-testid="home-hero-description">
              Intellix accompagne les PME vers des environnements modernes, efficaces et sécurisés grâce à Copilot, Purview, au Cloud Microsoft, à l’IA générative et à une politique Zero Trust.
            </p>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row">
              <Button asChild size="lg" className="rounded-full px-8 font-semibold" data-testid="home-primary-cta-button">
                <Link to="/contact">Planifier un échange <ArrowRight className="h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-full px-8" data-testid="home-services-cta-button">
                <Link to="/services">Découvrir nos services</Link>
              </Button>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <HeroVisual />
          </Reveal>
        </div>
      </section>

      <section className="border-y border-border/70 bg-card/40 px-5 py-8 sm:px-8" data-testid="home-stats-section">
        <div className="mx-auto grid max-w-7xl gap-6 md:grid-cols-5">
          {stats.map((stat) => (
            <div key={stat.value} className="flex items-start gap-4" data-testid={`home-stat-${stat.value.toLowerCase().replaceAll(" ", "-")}`}>
              <CheckCircle2 className="mt-1 h-5 w-5 text-primary" aria-hidden="true" />
              <div>
                <p className="font-display text-xl font-semibold">{stat.value}</p>
                <p className="mt-1 text-sm text-muted-foreground">{stat.label}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="px-5 py-24 sm:px-8 lg:py-32">
        <div className="mx-auto max-w-7xl">
          <SectionHeading
            eyebrow="Expertises"
            title="Un socle complet pour moderniser, sécuriser et automatiser."
            description="De Copilot à l’équipement IT, neuf domaines complémentaires pour aligner la technologie sur vos objectifs de croissance."
          />
          <div className="mt-14 grid auto-rows-[minmax(220px,auto)] gap-5 md:grid-cols-6" data-testid="home-services-bento-grid">
            {services.slice(0, 5).map((service, index) => {
              const Icon = service.icon;
              const span = index === 0 || index === 4 ? "md:col-span-4" : "md:col-span-2";
              return (
                <Reveal key={service.title} delay={index * 0.08} className={span}>
                  <Link
                    to="/services"
                    className="group flex h-full flex-col justify-between rounded-3xl border border-border/70 bg-card/60 p-7 transition-transform duration-300 hover:-translate-y-1 hover:border-primary/50"
                    data-testid={`home-service-card-${index + 1}`}
                  >
                    <Icon className="h-8 w-8 text-primary" aria-hidden="true" />
                    <div>
                      <h3 className="font-display text-2xl font-semibold tracking-tight">{service.title}</h3>
                      <p className="mt-4 text-sm leading-relaxed text-muted-foreground">{service.description}</p>
                    </div>
                  </Link>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-5 pb-24 sm:px-8 lg:pb-32">
        <Reveal className="mx-auto max-w-7xl">
          <div className="tracing-beam rounded-[2rem] bg-card p-8 sm:p-12 lg:p-16" data-testid="home-contact-cta-panel">
            <div className="grid gap-10 lg:grid-cols-[1fr_auto] lg:items-center">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary">Passage à l’action</p>
                <h2 className="mt-4 max-w-3xl text-3xl font-semibold tracking-tight sm:text-4xl">
                  Votre environnement numérique peut devenir un avantage compétitif.
                </h2>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground">
                  Parlons de vos usages, de vos risques et de vos priorités pour définir une trajectoire réaliste.
                </p>
              </div>
              <Button asChild size="lg" className="rounded-full px-8 font-semibold" data-testid="home-bottom-contact-button">
                <Link to="/contact">Demander un diagnostic</Link>
              </Button>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
